import { Box, IconButton } from "@mui/material";
import Image from "next/image";
import React, { useState, useEffect } from "react";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import CloseIcon from "@mui/icons-material/Close";
import { useAppSelector } from "@/hooks/useAppSelector";

const IMAGE_BASE = process.env.NEXT_PUBLIC_IMAGE_BASE;

interface ImageCarouselProps {
  onClose: () => void;
  displayedImage: string[];
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
  displayedImage,
  onClose,
}) => {
  const { selectedSKU } = useAppSelector((state) => state.home);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [skuImages, setSkuImages] = useState<string[]>(displayedImage || []);

  // Extract images based on selected SKU
  useEffect(() => {
    if (selectedSKU && displayedImage) {
      setSkuImages(displayedImage || []);
      setCurrentImageIndex(0);
    }
  }, [selectedSKU, displayedImage]);

  const handleImageNavigation = (direction: "prev" | "next") => {
    setCurrentImageIndex((prevIndex) => {
      if (direction === "prev") {
        return prevIndex === 0 ? skuImages.length - 1 : prevIndex - 1;
      } else {
        return prevIndex === skuImages.length - 1 ? 0 : prevIndex + 1;
      }
    });
  };

  return (
    <Box
      sx={{
        flex: 1,
        width: "100%",
        padding: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: "50px",
      }}
    >
      {/* Image Carousel */}
      <Box width="90%" py={2}>
        <Box
          py={2}
          position="relative"
          display="flex"
          justifyContent="center"
          bgcolor="white"
          borderRadius="6px"
          border="1px solid #e7e5eb"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseIcon
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "0",
              left: "-40px",
              color: "white",
              fontSize: "30px",
              cursor: "pointer",
            }}
          />
          <IconButton
            onClick={() => handleImageNavigation("prev")}
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              padding: "0",
            }}
          >
            <WestIcon
              sx={{
                background: "#283248",
                color: "white",
                fontSize: "35px",
                padding: "6px",
              }}
            />
          </IconButton>

          {skuImages.length > 0 ? (
            <Image
              quality={50}
              property="no"
              unoptimized
              src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_PRODUCTS_IMAGE/${skuImages[currentImageIndex]}`}
              alt="Product"
              height={450}
              width={80}
              style={{
                width: "100%",
                height: "450px",
                objectFit: "contain",
                borderRadius: "4px",
              }}
            />
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="450px"
            >
              <p>No Image Available</p>
            </Box>
          )}

          <IconButton
            onClick={() => handleImageNavigation("next")}
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              padding: "0",
            }}
          >
            <EastIcon
              sx={{
                background: "#283248",
                color: "white",
                fontSize: "35px",
                padding: "6px",
              }}
            />
          </IconButton>
        </Box>

        {/* Thumbnails */}
        <Box
          display="flex"
          width="fit-content"
          height={120}
          justifyContent="flex-start"
          sx={{ overflowX: "auto", py: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {skuImages.map((src, index) => (
            <Image
              quality={50}
              property="no"
              unoptimized
              key={index}
              src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${IMAGE_BASE}_PRODUCTS_IMAGE/${src}`}
              alt={`Thumbnail ${index + 1}`}
              width={120}
              height={50}
              onClick={() => setCurrentImageIndex(index)}
              style={{
                marginRight: index !== skuImages.length - 1 ? "20px" : "0",
                border:
                  currentImageIndex === index
                    ? "2px solid var(--color-blue)"
                    : "1px solid #e7e5eb",
                borderRadius: "6px",
                cursor: "pointer",
                objectFit: "contain",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ImageCarousel;
